<template>
  <div class="cf-grid cf-grid--2-grid-column-left-large">
    <div class="cf-flex cf-flex--row cf-flex--gap-12">
      <!-- Quantity -->
      <div class="cf-flex cf-flex--align-start cf-max-width-30">
        <h4
          v-if="!hideQuantity || item.quantity !== 1"
          class="cf-title cf-title--4"
        >
          {{ item.quantity }}
        </h4>
      </div>

      <!-- Image -->
      <img
        v-if="imageUrl"
        class="cf-image cf-image--thumbnail cf-mobile-hide"
        :src="imageUrl"
        onerror="this.src='/images/image-not-found.png';"
      >

      <div class="cf-flex cf-flex--column cf-flex--gap-8">
        <div class="cf-flex cf-flex--row cf-flex--gap-8 cf-full-width">
          <!-- Item Description -->
          <h4 class="cf-title cf-title--4 cf-no-wrap-ellipsis-multi-line">
            {{ item.itemDescription }}
          </h4>

          <!-- Item Rating -->
          <button
            v-if="isItemRatingDisplayed"
            class="cf-button cf-button--primary cf-button--auto cf-height-25"
            type="button"
            @click="rateItemClickHandler"
          >
            <template v-if="storedItemRating">
              {{ storedItemRating }}

              <span class="cf-icon cf-icon--sm cf-icon--white fa fa-star" />
            </template>

            <template v-else>
              {{ itemRateButtonContent }}
            </template>
          </button>

          <!-- Item available schedule tooltip -->
          <tippy
            v-if="item.schedulesPerDay && !isReceiptView"
            arrow
            theme="dark"
          >
            <button
              type="button"
              class="cf-button cf-button--smallest cf-button--tertiary"
              tabindex="-1"
            >
              <span class="cf-icon fa fa-info" />
            </button>

            <template #content>
              <div>
                <div class="layout layout--icon half-spacing">
                  <span class="icon far fa-clock dark" />

                  <h5 class="title title--6 dark">Available</h5>
                </div>

                <ul class="bullet-list no-spacing">
                  <li
                    v-for="(schedules, day) in item.schedulesPerDay"
                    :key="day"
                  >
                    <div class="layout layout--horizontal layout--align-start no-spacing-children">
                      <p class="no-shrink">
                        {{ day.slice(0, 3) }}:
                      </p>

                      <p class="comma-list comma-list--pipe auto-spacing-left dark right">
                        <span
                          v-for="schedule in schedules"
                          :key="schedule.display"
                          class="comma-list__item"
                        >
                          {{ schedule.display }}
                        </span>
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </template>
          </tippy>
        </div>

        <!-- Item Modifiers -->
        <div
          v-if="modifiers.standardModifiers.length > 0"
          class="cf-flex cf-flex--row cf-flex--gap-6 cf-flex--wrap"
        >
          <p
            v-for="(modifier, index) in modifiers.standardModifiers"
            :key="modifier.menuItemId"
            class="cf-subtitle cf-subtitle--3"
          >
            {{ getModifierContent(modifier, index, modifiers.standardModifiers) }}
          </p>
        </div>

        <!-- Item Nested Modifiers -->
        <div
          v-if="modifiers.nestedModifiers.length > 0"
          class="cf-flex cf-flex--row cf-flex--gap-6 cf-flex--wrap"
        >
          <p
            v-for="nestedModifier in modifiers.nestedModifiers"
            :key="nestedModifier.menuItemId"
            class="cf-subtitle cf-subtitle--3"
          >
            <span class="cf-subtitle cf-subtitle--3 cf-subtitle--heavy">
              {{ nestedModifier.itemDescription }}
            </span>

            <span
              v-for="(modifier, index) in nestedModifier.modifiers"
              :key="modifier.menuItemId"
              class="cf-subtitle cf-subtitle--3"
            >
              {{ getModifierContent(modifier, index, nestedModifier.modifiers) }}
            </span>
          </p>
        </div>

        <!-- Item Special Instructions -->
        <p
          v-if="item.specialInstructions"
          class="cf-subtitle cf-subtitle--3"
        >
          {{ orderItemInstructionsContent }}: {{ item.specialInstructions }}
        </p>
      </div>
    </div>

    <div class="cf-flex cf-flex--column cf-flex--column-justify-start cf-flex--gap-12">
      <!-- Item Price -->
      <p
        v-show="!hidePrices"
        :class="['cf-subtitle cf-subtitle--2 cf-text-align-right', { 'cf-subtitle--heavy': !isReceiptView }]"
      >
        {{ $dollars(item.priceTotal) }}
      </p>

      <!-- Actionable Buttons -->
      <div
        v-if="!isReadOnly && !isReceiptView"
        class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-end cf-flex--gap-8"
      >
        <cf-favorite-button
          v-if="!isFavoritingDisabled"
          :is-receipt="isReadOnly"
          :item="item"
          :modifiers="item.modifiers"
          :special-instructions="item.specialInstructions"
        />

        <button
          type="button"
          title="Edit this item"
          tabindex="-1"
          class="cf-button cf-button--tertiary cf-button--xs"
          @click="editItemClickHandler(item)"
        >
          Edit
        </button>

        <button
          type="button"
          title="Remove this item"
          tabindex="-1"
          :class="['cf-button cf-button--tertiary cf-button--fixed-24', { 'cf-button--loading': isRemovingItem }]"
          @click="removeItemClickHandler(item)"
        >
          <span class="cf-icon far fa-trash-alt secondary" />
        </button>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters, mapState } from 'vuex';
  import { v4 as uuid } from 'uuid';

  import CfFavoriteButton from '@/view/components/shared/cf-favorite-button/index.vue';


  export default {
    name: 'CfOrderItem',

    components: {
      CfFavoriteButton
    },

    props: {
      item: {
        type: Object,
        required: true
      },

      order: {
        type: Object,
        required: true
      },

      hideQuantity: {
        type: Boolean,
        default: false
      },

      hidePrices: {
        type: Boolean,
        default: false
      },

      isReadOnly: {
        type: Boolean,
        default: false
      },

      isFavoritingDisabled: {
        type: Boolean,
        default: false
      },

      isReceiptView: {
        type: Boolean,
        default: false
      },

      isYourOrderItem: {
        type: Boolean,
        default: true
      },

      isItemRatingEnabled: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      ...mapState('orderHistory', ['ratingsForOrders']),
      ...mapGetters('content', ['content']),
      ...mapGetters('merchant', ['merchant']),

      isItemRatingDisplayed() {
        return this.isItemRatingEnabled && this.isReceiptView && this.isYourOrderItem;
      },

      storedItemRating() {
        const storedOrderRatings = this.ratingsForOrders[this.order.orderId];

        if (storedOrderRatings) {
          const storedMenuItemRating = storedOrderRatings[this.item.menuItemId]?.rating;

          return storedMenuItemRating || this.item.accountRating;
        }

        return this.item.accountRating;
      },

      itemRateButtonContent() {
        return this.content.ItemRating.metadata.cta;
      },

      imageUrl() {
        const image = this.item.images?.find(img => img.label === 'menu');

        return image ? image.url : null;
      },

      modifiers() {
        const nestedModifiers = [];

        const standardModifiers = [];

        this.item.modifiers.forEach((modifier) => {
          const _modifier = { ...modifier, menuItemId: modifier.menuItemId ? modifier.menuItemId : uuid() };

          if (modifier.itemDescription) {
            if (modifier.modifiers && modifier.modifiers.length > 0) {
              nestedModifiers.push(_modifier);
            }

            else {
              standardModifiers.push(_modifier);
            }
          }
        });

        const filteredNestedModifiers = nestedModifiers.filter(nestedMod => nestedMod.itemDescription);

        return { standardModifiers, nestedModifiers: filteredNestedModifiers };
      },

      isRemovingItem() {
        if (this.order.removingItemIds) {
          return this.order.removingItemIds.indexOf(this.item.orderItemId) > -1;
        }

        return false;
      },

      orderItemInstructionsContent() {
        return this.content.ItemLevelInstructions.metadata.inputPlaceholder;
      }
    },

    methods: {
      async editItemClickHandler(item) {
        const selectItemPayload = {
          itemId: item.menuItemId,
          orderItemId: item.orderItemId,
          favoriteItemId: item.favoriteItemId
        };

        await this.$store.dispatch('menu/selectItem', selectItemPayload);

        this.$openModal('menuItem');
      },

      removeItemClickHandler(item) {
        this.$store.dispatch('orders/removeItem', {
          orderItemId: item.orderItemId,
          orderItems: [item]
        });
      },

      getModifierContent(modifier, index, arr) {
        const modifierQty = modifier.quantity > 1 ? `(${modifier.quantity})` : '';
        const commaDelimeter = index === arr.length - 1 ? '' : ',';

        return `${modifier.itemDescription.trim()}${modifierQty}${commaDelimeter} `;
      },

      rateItemClickHandler() {
        const modalPayload = { item: this.item, itemRating: this.storedItemRating, order: this.order };

        this.$openModal('order-item-rating-modal', modalPayload);
      }
    }
  };
</script>t
